import { useEffect, useRef, useState } from "react";
import { ColumnDTO, NoteDTO, NOTE_SIZE } from "@retrospecter/shared/types";
import { NoteCard, MoveDirection } from "./NoteCard";
import { accentFor } from "./columnAccents";
import { CANVAS_PADDING, canvasHeightFor, fitToCanvas } from "./noteLayout";
import { NoteDrag, NoteDragStart } from "../../hooks/useNoteDrag";
import { useTranslation } from "../../i18n";

/** Where a new note is being written, in canvas coordinates. */
export interface ComposeSpot {
  x: number;
  y: number;
}

interface ColumnProps {
  column: ColumnDTO;
  columnIndex: number;
  /** Scopes column translations to this board's format. */
  templateName: string;
  /** This column's notes, back to front. */
  notes: NoteDTO[];
  canContribute: boolean;
  /** True while a blank note from the pile is being dragged anywhere. */
  isDraggingNewNote: boolean;
  /** Where on this canvas a note is being written, or null when none is. */
  composeAt: ComposeSpot | null;
  onCompose: (columnId: string, spot: ComposeSpot) => void;
  onCancelCompose: () => void;
  canVote: boolean;
  canMove: boolean;
  isOwner: boolean;
  isCompleted: boolean;
  /** The board-wide drag, so the note being carried follows the pointer. */
  drag: NoteDrag | null;
  onDragStartNote: (start: NoteDragStart) => void;
  onResizeNote: (noteId: string, size: { width: number; height: number }) => void;
  onGroupNote: (noteId: string, parentId: string) => void;
  onKeyboardMove: (noteId: string, direction: MoveDirection, toColumn: boolean) => void;
  onAddNote: (
    columnId: string,
    text: string,
    spot: ComposeSpot & { width: number; height: number }
  ) => Promise<void>;
  onChanged: () => void;
}

/** Tracks a canvas's width, which is what every note is fitted against. */
function useCanvasWidth(ref: React.RefObject<HTMLElement>): number {
  const [width, setWidth] = useState(0);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const measure = () => setWidth(element.getBoundingClientRect().width);
    measure();

    // Columns are a responsive grid, so a canvas changes width without the
    // window ever resizing — for instance when the board gains a column.
    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", measure);
      return () => window.removeEventListener("resize", measure);
    }

    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [ref]);

  return width;
}

export function Column({
  column,
  columnIndex,
  templateName,
  notes,
  canContribute,
  isDraggingNewNote,
  composeAt,
  onCompose,
  onCancelCompose,
  canVote,
  canMove,
  isOwner,
  isCompleted,
  drag,
  onDragStartNote,
  onResizeNote,
  onGroupNote,
  onKeyboardMove,
  onAddNote,
  onChanged,
}: ColumnProps) {
  const { t, columnTitle, columnHint } = useTranslation();
  const accent = accentFor(columnIndex);
  const title = columnTitle(column.title, templateName);
  const hint = columnHint(column.title, column.hint, templateName);

  // The badge counts every note in the column, grouped ones included — a group
  // is a way of arranging notes, not a way of hiding them from the tally.
  const noteCount = notes.reduce(
    (total, note) => total + 1 + note.children.length,
    0
  );

  const canvasRef = useRef<HTMLDivElement>(null);
  const canvasWidth = useCanvasWidth(canvasRef);

  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);

  // A blank note from the pile still arrives by native drag-and-drop, so the
  // whole card highlights while one is over it.
  const [pileOver, setPileOver] = useState(false);
  const isDropTarget = pileOver || drag?.overColumnId === column.id;

  const placed = notes.map((note) => ({
    note,
    placement: fitToCanvas(note, canvasWidth),
  }));

  const composerSize = { width: NOTE_SIZE.defaultWidth, height: NOTE_SIZE.defaultHeight };
  const canvasHeight = canvasHeightFor([
    ...placed.map((item) => item.placement),
    ...(composeAt ? [{ ...composeAt, ...composerSize }] : []),
  ]);

  /** Turns a pointer position into a spot on this canvas. */
  const spotFromEvent = (event: { clientX: number; clientY: number }): ComposeSpot => {
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return { x: CANVAS_PADDING, y: CANVAS_PADDING };

    // The pointer holds the top edge of the note, near its middle — that is
    // where the pile's drag image sat, so the note lands where it looked.
    const x = event.clientX - rect.left - composerSize.width / 2;
    const y = event.clientY - rect.top - 16;

    return {
      x: Math.max(0, Math.min(x, Math.max(0, rect.width - composerSize.width))),
      y: Math.max(0, y),
    };
  };

  const submit = async () => {
    if (!text.trim() || !composeAt) return;
    setSaving(true);
    try {
      await onAddNote(column.id, text.trim(), { ...composeAt, ...composerSize });
      setText("");
      onCancelCompose();
    } finally {
      setSaving(false);
    }
  };

  const cancel = () => {
    setText("");
    onCancelCompose();
  };

  return (
    <section
      aria-label={title}
      data-column-region={column.id}
      onDragOver={(event) => {
        if (!isDraggingNewNote) return;
        event.preventDefault();
        event.dataTransfer.dropEffect = "copy";
        setPileOver(true);
      }}
      onDragLeave={(event) => {
        // Ignore moves between children of this column.
        if (event.currentTarget.contains(event.relatedTarget as Node)) return;
        setPileOver(false);
      }}
      onDrop={(event) => {
        if (!isDraggingNewNote) return;
        event.preventDefault();
        setPileOver(false);
        onCompose(column.id, spotFromEvent(event));
      }}
      className={`flex flex-col rounded-2xl border transition-all duration-200 ${
        isDropTarget
          ? `border-transparent bg-surface shadow-raised ring-2 ${accent.dropRing}`
          : "border-line bg-surface shadow-subtle"
      }`}
    >
      <div className={`h-1 rounded-t-2xl ${accent.bar}`} aria-hidden="true" />

      <header
        className={`bg-gradient-to-b to-transparent px-4 pb-2.5 pt-3 ${accent.glow}`}
      >
        <div className="flex items-center justify-between gap-3">
          <h2 className="truncate text-[0.8rem] font-bold uppercase tracking-[0.08em] text-ink-soft">
            {title}
          </h2>
          <span
            className={`rounded-full px-2 py-0.5 text-xs font-semibold tabular-nums ${accent.pill}`}
          >
            {noteCount}
          </span>
        </div>

        {/* What belongs in this column. Column titles alone are ambiguous, and
            a team meeting a format for the first time shouldn't have to guess. */}
        {hint && (
          <p className="mt-1 text-xs leading-snug text-ink-faint">{hint}</p>
        )}
      </header>

      {/* The canvas. Notes are placed on it rather than stacked in a list, so
          a team can cluster, spread out and size their post-its however the
          conversation actually goes. */}
      <div
        ref={canvasRef}
        data-column-canvas={column.id}
        data-testid={`canvas-${column.id}`}
        // Tall enough for its own notes, and then stretched to fill the column
        // card: the columns in a row are the same height, so a canvas that
        // stopped at its content would leave the rest of a half-empty column
        // as dead space that a note dragged across simply fell into.
        style={{ minHeight: canvasHeight }}
        className="note-canvas relative mx-3 mb-3 flex-1 rounded-xl"
      >
        {placed.map(({ note, placement }, index) => {
          const canManage = !isCompleted && (note.isMine || isOwner);
          const isDragging = drag?.noteId === note.id;
          const previous = notes[index - 1];

          return (
            <NoteCard
              key={note.id}
              note={note}
              accent={accent}
              placement={placement}
              zIndex={index + 1}
              canManage={canManage}
              canVote={canVote}
              canMove={canMove}
              isDragging={isDragging}
              floatingAt={
                isDragging && drag ? { left: drag.left, top: drag.top } : null
              }
              onDragStart={(start) =>
                onDragStartNote({ ...start, noteId: note.id, columnId: column.id })
              }
              onResize={(size) => onResizeNote(note.id, size)}
              onKeyboardMove={(direction, toColumn) =>
                onKeyboardMove(note.id, direction, toColumn)
              }
              isGroupTarget={drag?.groupTargetId === note.id}
              onGroupWithPrevious={
                canMove && previous ? () => onGroupNote(note.id, previous.id) : undefined
              }
              canManageOthers={!isCompleted && isOwner}
              onChanged={onChanged}
            />
          );
        })}

        {canContribute && composeAt && (
          <div
            style={{
              ["--lane" as string]: accent.lane,
              left: composeAt.x,
              top: composeAt.y,
              width: composerSize.width,
              minHeight: composerSize.height,
            }}
            className="post-it animate-popIn absolute z-50 flex flex-col gap-2 rounded-xl border p-2.5 shadow-float"
          >
            <textarea
              value={text}
              onChange={(event) => setText(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) {
                  event.preventDefault();
                  submit();
                }
                if (event.key === "Escape") cancel();
              }}
              placeholder={t("column.notePlaceholder")}
              rows={3}
              autoFocus
              aria-label={t("column.newNoteFor", { column: title })}
              data-testid="note-input"
              className="input-field flex-1 resize-none px-2.5 py-2 text-sm"
            />
            <div className="flex gap-2">
              <button
                onClick={submit}
                disabled={saving || !text.trim()}
                data-testid="save-note"
                className="btn-accent flex-1 px-3 py-1.5 text-sm"
              >
                {saving ? t("column.adding") : t("column.saveNote")}
              </button>
              <button
                onClick={cancel}
                aria-label={t("column.cancelNote")}
                className="btn-ghost px-3 py-1.5 text-sm"
              >
                ✕
              </button>
            </div>
          </div>
        )}

        {notes.length === 0 && !composeAt && (
          <p className="pointer-events-none absolute inset-0 flex items-center justify-center text-center text-xs text-ink-faint">
            {isDropTarget ? t("column.dropHere") : t("column.empty")}
          </p>
        )}
      </div>
    </section>
  );
}
